import { useEffect, useMemo, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import {
  AlertTriangle,
  CheckCircle,
  Clock,
  Download,
  Play,
  RefreshCw,
  X,
  XCircle,
} from 'lucide-react';
import {
  fetchLatestReplay,
  fetchReplayReportDetail,
  resetReplayState,
  startReplay,
} from '../../redux/slice/queryLogReplaySlice';
import {
  clearStep5Intent,
  fetchStep5Status,
} from '../../redux/slice/step5Slice';
import {
  RootState,
  useAppDispatch,
  useAppSelector,
} from '../../redux/store';
import { getReplay } from '../../services/queryLogReplayApi';
import {
  ReplayRecord,
  ReplayScaleFactor,
} from '../../types/queryLogReplay';
import RdsLockQueuedBanner from '../rdsLock/RdsLockQueuedBanner';

// Status-driven polling cadence. We poll while the replay is still progressing through ECS
// lifecycle transitions and stop once it reaches a terminal state. 30s strikes a balance
// between freshness and not hammering API Gateway: a real replay runs for hours.
const POLL_INTERVAL_MS = 30_000;

const SCALE_OPTIONS: ReplayScaleFactor[] = ['1x', '10x'];

function formatDateTime(value: string | null): string {
  if (!value) return '\u2014';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleString('en-US', {
    month: 'short',
    day: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  });
}

function formatDurationSeconds(seconds: number | null | undefined): string {
  if (seconds == null || Number.isNaN(seconds)) return '\u2014';
  const total = Math.max(0, Math.round(seconds));
  const days = Math.floor(total / 86400);
  const hours = Math.floor((total % 86400) / 3600);
  const minutes = Math.floor((total % 3600) / 60);
  const secs = total % 60;
  // Query log spans are commonly 24h+, so show days before falling through to h/m/s.
  if (days > 0) return `${days}d ${hours}h ${minutes}m`;
  if (hours > 0) return `${hours}h ${minutes}m`;
  if (minutes > 0) return `${minutes}m ${secs}s`;
  return `${secs}s`;
}

function formatMs(ms: number | null | undefined): string {
  if (ms == null || Number.isNaN(ms)) return '\u2014';
  if (ms >= 1000) return `${(ms / 1000).toFixed(2)}s`;
  return `${ms.toFixed(2)}ms`;
}


function StatusBadge({ status }: { status: ReplayRecord['status'] }) {
  switch (status) {
    case 'PENDING':
      return (
        <div className="flex items-center gap-2 px-3 py-1 bg-gray-100 text-gray-700 rounded-full text-sm font-medium">
          <div className="w-2 h-2 bg-gray-500 rounded-full animate-pulse" />
          PENDING
        </div>
      );
    case 'RUNNING':
      return (
        <div className="flex items-center gap-2 px-3 py-1 bg-blue-100 text-blue-700 rounded-full text-sm font-medium">
          <div className="w-2 h-2 bg-blue-500 rounded-full animate-pulse" />
          RUNNING
        </div>
      );
    case 'COMPLETED':
      return (
        <div className="flex items-center gap-2 px-3 py-1 bg-green-100 text-green-700 rounded-full text-sm font-medium">
          <CheckCircle className="w-4 h-4" />
          COMPLETED
        </div>
      );
    case 'FAILED':
      return (
        <div className="flex items-center gap-2 px-3 py-1 bg-red-100 text-red-700 rounded-full text-sm font-medium">
          <XCircle className="w-4 h-4" />
          FAILED
        </div>
      );
    default:
      return null;
  }
}

function QueryLogReplay() {
  const dispatch = useAppDispatch();

  const projectId = useAppSelector(
    (state: RootState) => state.userProject.currentProject.project_id
  );
  const runId = useAppSelector(
    (state: RootState) => state.userProject.currentRunId
  );

  const replayState = useAppSelector(
    (state: RootState) => state.queryLogReplay
  );
  const {
    latestReplay,
    report,
    reportForReplayId,
    isLoadingReplay,
    isLoadingReport,
    isTriggering,
    triggerError,
    loadError,
  } = replayState;

  // Step 5 intent / scaling state lives in a sibling slice. The replay row in queryLogReplay
  // tells us whether a REPLAY_PROCESSOR row exists; the step5 slice tells us whether the
  // user has QUEUED or had a REJECTED intent (which never gets a row).
  const step5State = useAppSelector((state: RootState) => state.step5);
  const replayIntent = step5State.status?.intent?.replay ?? null;
  const scalingStatus = step5State.status?.scaling?.status;
  const isCancellingIntent = step5State.isCancellingIntent === 'replay';

  const [scaleFactor, setScaleFactor] = useState<ReplayScaleFactor>('10x');
  const [isDownloadingReport, setIsDownloadingReport] = useState(false);
  const [downloadError, setDownloadError] = useState<string | null>(null);

  // Initial fetch + clean up when scope changes (project/run switch).
  useEffect(() => {
    dispatch(resetReplayState());
    if (projectId > 0 && runId != null && runId > 0) {
      dispatch(fetchLatestReplay(projectId, runId));
      dispatch(fetchStep5Status(projectId, runId, { silent: true }));
    }
  }, [dispatch, projectId, runId]);

  // Poll Step 5 status while replay is queued (no row yet) so we flip off the queue banner
  // automatically once the backend dispatches the task. Once a REPLAY_PROCESSOR row exists,
  // the existing latestReplay poll takes over.
  useEffect(() => {
    if (projectId <= 0 || runId == null || runId <= 0) return;
    const shouldPollStep5 =
      replayIntent?.status === 'QUEUED' ||
      scalingStatus === 'PENDING' ||
      scalingStatus === 'RUNNING' ||
      step5State.status?.rds_lock?.status === 'QUEUED';
    if (!shouldPollStep5) return;
    const id = window.setInterval(() => {
      dispatch(fetchStep5Status(projectId, runId, { silent: true }));
    }, POLL_INTERVAL_MS);
    return () => {
      window.clearInterval(id);
    };
  }, [dispatch, projectId, runId, replayIntent?.status, scalingStatus, step5State.status?.rds_lock?.status]);

  // Background poll while PENDING/RUNNING. We stop polling once we reach a terminal state
  // (COMPLETED/FAILED) to avoid leaking timers. Note: status writebacks from the replay
  // ECS task are not currently wired up — rows stay PENDING until a downstream mechanism
  // updates them, so this poll may loop indefinitely until that's reinstated.
  useEffect(() => {
    const status = latestReplay?.status;
    if (status !== 'PENDING' && status !== 'RUNNING') return;
    if (projectId <= 0 || runId == null || runId <= 0) return;

    const intervalId = window.setInterval(() => {
      dispatch(fetchLatestReplay(projectId, runId, { silent: true }));
    }, POLL_INTERVAL_MS);

    return () => {
      window.clearInterval(intervalId);
    };
  }, [dispatch, projectId, runId, latestReplay?.status]);

  // When the replay reaches COMPLETED, fetch the full report once so we can show
  // top-contested-tables (the project_data row only has the summary metrics slice).
  useEffect(() => {
    if (!latestReplay) return;
    if (latestReplay.status !== 'COMPLETED') return;
    if (reportForReplayId === latestReplay.replay_id && report) return;
    if (projectId <= 0 || runId == null || runId <= 0) return;
    dispatch(
      fetchReplayReportDetail(projectId, runId, latestReplay.replay_id)
    );
  }, [
    dispatch,
    projectId,
    runId,
    latestReplay,
    report,
    reportForReplayId,
  ]);

  const handleStartReplay = async () => {
    if (projectId <= 0 || runId == null || runId <= 0) return;
    await dispatch(startReplay(projectId, runId, scaleFactor) as any);
    // Pull the latest intent state so the UI flips to QUEUED (if scaling/5a still running)
    // or stays on the normal replay row path (if the task launched immediately).
    dispatch(fetchStep5Status(projectId, runId, { silent: true }));
  };

  const handleRetry = () => {
    // Same as start — the trigger Lambda happily creates a fresh PENDING record when the
    // previous one is FAILED (only PENDING/RUNNING are blocked as concurrent).
    handleStartReplay();
  };

  const handleCancelQueue = () => {
    if (projectId <= 0 || runId == null || runId <= 0) return;
    dispatch(clearStep5Intent(projectId, runId, 'replay'));
  };

  /**
   * Generate a fresh presigned URL on every download click. The URL the slice already cached
   * may have expired by the time the user clicks (default presign TTL = 15 min).
   */
  const handleDownloadReport = async () => {
    if (!latestReplay || projectId <= 0 || runId == null || runId <= 0) return;
    setIsDownloadingReport(true);
    setDownloadError(null);
    try {
      const detail = await getReplay(projectId, runId, latestReplay.replay_id);
      const pdfUrl = detail.report_pdf_presigned_url;
      if (!pdfUrl) {
        setDownloadError(
          'Query log report PDF is not ready yet. Please try again in a moment.'
        );
        return;
      }
      const link = document.createElement('a');
      link.href = pdfUrl;
      link.download =
        detail.report_pdf_filename || `query-log-report-${latestReplay.replay_id}.pdf`;
      link.target = '_blank';
      link.rel = 'noopener noreferrer';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (err: any) {
      setDownloadError(
        err?.displayMessage ||
          err?.message ||
          'Failed to download the replay report.'
      );
    } finally {
      setIsDownloadingReport(false);
    }
  };

  const metrics = latestReplay?.metrics;

  const topContestedTables = useMemo(() => {
    if (!report?.top_contested_tables) return [];
    return report.top_contested_tables.slice(0, 10);
  }, [report]);

  // ---------------------------------------------------------------------------
  // Loading guard — only show the spinner on the initial mount fetch. Subsequent silent
  // polls keep the UI stable.
  // ---------------------------------------------------------------------------
  if (isLoadingReplay && latestReplay === null) {
    return (
      <div className="bg-[#f3f4f6] -mx-4 -mt-4 px-4 py-6 min-h-full font-sans">
        <div className="bg-white rounded-lg shadow-sm border border-solid border-gray-200 p-12 flex items-center justify-center">
          <div className="flex items-center gap-3 text-gray-500">
            <svg
              className="animate-spin w-5 h-5"
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
            >
              <circle
                className="opacity-25"
                cx="12"
                cy="12"
                r="10"
                stroke="currentColor"
                strokeWidth="4"
              />
              <path
                className="opacity-75"
                fill="currentColor"
                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
              />
            </svg>
            <span className="text-sm">Loading replay status...</span>
          </div>
        </div>
      </div>
    );
  }

  // ---------------------------------------------------------------------------
  // Rejected — Step 5b will never run for this run because a prerequisite (scaling /
  // perf report) failed. Show the reason and a dismiss button that clears the intent.
  // ---------------------------------------------------------------------------
  if (replayIntent?.status === 'REJECTED' && !latestReplay) {
    return (
      <div className="bg-[#f3f4f6] -mx-4 -mt-4 px-4 py-6 min-h-full font-sans">
        <div className="bg-white rounded-lg shadow-sm border border-solid border-gray-200 p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-xl font-bold text-[#1e1b4b]">
              <FormattedMessage id="steps.5.replay.heading" />
            </h2>
            <div className="flex items-center gap-2 px-3 py-1 bg-red-100 text-red-700 rounded-full text-sm font-medium">
              <XCircle className="w-4 h-4" />
              REJECTED
            </div>
          </div>
          <div className="bg-red-50 border border-solid border-red-200 rounded-lg p-4 mb-4">
            <p className="text-sm text-red-800">
              <span className="font-medium">
                <FormattedMessage id="steps.5.replay.rejectedLabel" />:{' '}
              </span>
              {replayIntent.rejected_reason ||
                'A prerequisite step failed; please create a new run.'}
            </p>
          </div>
          <div className="flex justify-end">
            <button
              onClick={handleCancelQueue}
              disabled={isCancellingIntent}
              className="flex items-center gap-2 bg-gray-100 text-gray-700 px-4 py-2 rounded-lg hover:bg-gray-200 transition-colors font-medium text-sm disabled:opacity-50"
            >
              <X className="w-4 h-4" />
              <FormattedMessage id="steps.5.replay.dismissButton" />
            </button>
          </div>
        </div>
      </div>
    );
  }

  // ---------------------------------------------------------------------------
  // Queued — scaling (or a 5a perf report) is still running. The task will fire automatically
  // once the prerequisite completes; user can cancel the queue to release the slot.
  // ---------------------------------------------------------------------------
  if (replayIntent?.status === 'QUEUED' && !latestReplay) {
    return (
      <div className="bg-[#f3f4f6] -mx-4 -mt-4 px-4 py-6 min-h-full font-sans">
        <div className="bg-white rounded-lg shadow-sm border border-solid border-gray-200 p-6">
          <RdsLockQueuedBanner rdsLock={step5State.status?.rds_lock} />
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-xl font-bold text-[#1e1b4b]">
              <FormattedMessage id="steps.5.replay.heading" />
            </h2>
            <div className="flex items-center gap-2 px-3 py-1 bg-gray-100 text-gray-700 rounded-full text-sm font-medium">
              <div className="w-2 h-2 bg-gray-500 rounded-full animate-pulse" />
              QUEUED
            </div>
          </div>
          <div className="bg-blue-50 border border-solid border-blue-200 rounded-lg p-4 mb-4 flex items-start gap-3">
            <Clock className="w-5 h-5 text-blue-600 mt-0.5 flex-shrink-0" />
            <p className="text-sm text-blue-900">
              {replayIntent.queued_reason ||
                'Your replay is queued and will run automatically.'}
            </p>
          </div>
          <div className="flex justify-end">
            <button
              onClick={handleCancelQueue}
              disabled={isCancellingIntent}
              className="flex items-center gap-2 bg-gray-100 text-gray-700 px-4 py-2 rounded-lg hover:bg-gray-200 transition-colors font-medium text-sm disabled:opacity-50"
            >
              <X className="w-4 h-4" />
              <FormattedMessage id="steps.5.replay.cancelQueueButton" />
            </button>
          </div>
        </div>
      </div>
    );
  }

  // ---------------------------------------------------------------------------
  // Idle (no replay yet) — show the configurator + Start Replay CTA.
  // ---------------------------------------------------------------------------
  if (!latestReplay) {
    return (
      <div className="bg-[#f3f4f6] -mx-4 -mt-4 px-4 py-6 min-h-full font-sans">
        <div className="bg-white rounded-lg shadow-sm border border-solid border-gray-200 p-6">
          <div className="mb-6">
            <h2 className="text-xl font-bold text-[#1e1b4b] mb-2">
              <FormattedMessage id="steps.5.replay.heading" />
            </h2>
            <p className="text-gray-600 text-sm">
              <FormattedMessage id="steps.5.replay.idleDescription" />
            </p>
          </div>

          <div className="mb-6">
            <label
              className="block text-sm font-medium text-gray-700 mb-2"
              htmlFor="replay-scale-target"
            >
              <FormattedMessage id="steps.5.replay.scaleTargetLabel" />
            </label>
            <select
              id="replay-scale-target"
              value={scaleFactor}
              onChange={(e) =>
                setScaleFactor(e.target.value as ReplayScaleFactor)
              }
              className="w-48 px-3 py-2 border border-solid border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1e1b4b] focus:border-transparent text-sm bg-white"
              disabled={isTriggering}
            >
              {SCALE_OPTIONS.map((option) => (
                <option key={option} value={option}>
                  {option}
                </option>
              ))}
            </select>
          </div>

          <div className="border-t border-solid border-gray-200 pt-4 mb-6">
            <div className="flex items-center gap-2 text-sm text-gray-600">
              <Clock className="w-4 h-4" />
              <span>
                <FormattedMessage id="steps.5.replay.estimatedDurationNote" />
              </span>
            </div>
          </div>

          <div className="bg-yellow-50 border border-solid border-yellow-200 rounded-lg p-4 mb-6">
            <div className="flex items-start gap-3">
              <AlertTriangle className="w-5 h-5 text-yellow-600 mt-0.5 flex-shrink-0" />
              <div className="text-sm text-yellow-800">
                <p className="font-medium mb-1">
                  <FormattedMessage id="steps.5.replay.notice.title" />
                </p>
                <ul className="list-disc list-inside space-y-1 text-yellow-700">
                  <li>
                    <FormattedMessage id="steps.5.replay.notice.bullet1" />
                  </li>
                  <li>
                    <FormattedMessage id="steps.5.replay.notice.bullet2" />
                  </li>
                  <li>
                    <FormattedMessage id="steps.5.replay.notice.bullet3" />
                  </li>
                </ul>
              </div>
            </div>
          </div>

          {triggerError && (
            <div className="bg-red-50 border border-solid border-red-200 rounded-lg p-3 mb-4 text-sm text-red-700">
              {triggerError}
            </div>
          )}

          {loadError && !triggerError && (
            <div className="bg-red-50 border border-solid border-red-200 rounded-lg p-3 mb-4 text-sm text-red-700">
              {loadError}
            </div>
          )}

          <div className="flex justify-end">
            <button
              onClick={handleStartReplay}
              disabled={isTriggering || scalingStatus === 'FAILED'}
              className="flex items-center gap-2 bg-[#1e1b4b] text-white px-6 py-2 rounded-lg hover:bg-blue-900 transition-colors font-medium text-sm disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <Play className="w-4 h-4" />
              {isTriggering ? (
                <FormattedMessage id="steps.5.replay.startingButton" />
              ) : scalingStatus === 'PENDING' ||
                scalingStatus === 'RUNNING' ||
                scalingStatus === 'NOT_STARTED' ? (
                <FormattedMessage id="steps.5.replay.queueButton" />
              ) : (
                <FormattedMessage id="steps.5.replay.startButton" />
              )}
            </button>
          </div>
        </div>
      </div>
    );
  }

  // ---------------------------------------------------------------------------
  // Running / Pending — show status badge, started_at, and a brief in-progress notice.
  // We intentionally do NOT render live counters here: the backend does not expose query-level
  // progress mid-run today, and faking it would be misleading. The completion handler updates
  // the record once the task transitions to RUNNING / STOPPED.
  // ---------------------------------------------------------------------------
  if (latestReplay.status === 'PENDING' || latestReplay.status === 'RUNNING') {
    return (
      <div className="bg-[#f3f4f6] -mx-4 -mt-4 px-4 py-6 min-h-full font-sans">
        <div className="bg-white rounded-lg shadow-sm border border-solid border-gray-200 p-6">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-xl font-bold text-[#1e1b4b]">
              <FormattedMessage id="steps.5.replay.heading" />
            </h2>
            <StatusBadge status={latestReplay.status} />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
            <div>
              <p className="text-sm text-gray-500">
                <FormattedMessage id="steps.5.replay.runningStartedLabel" />
              </p>
              <p className="font-medium text-gray-900">
                {formatDateTime(
                  latestReplay.started_at || latestReplay.created_at
                )}
              </p>
            </div>
            <div>
              <p className="text-sm text-gray-500">
                <FormattedMessage id="steps.5.replay.scaleFactorLabel" />
              </p>
              <p className="font-medium text-gray-900">
                {latestReplay.scale_factor}
              </p>
            </div>
            <div>
              <p className="text-sm text-gray-500">
                <FormattedMessage id="steps.5.replay.queryLogDurationLabel" />
              </p>
              <p className="font-medium text-gray-900">
                {latestReplay.log_duration_seconds != null ? (
                  formatDurationSeconds(latestReplay.log_duration_seconds)
                ) : (
                  // Fallback only fires when the trigger Lambda couldn't compute the span
                  // (e.g. empty / unsorted log). Keeps the running screen useful instead of
                  // rendering an em-dash.
                  <FormattedMessage id="steps.5.replay.queryLogDurationUnknown" />
                )}
              </p>
            </div>
          </div>

          <div className="bg-gray-50 border border-solid border-gray-200 rounded-lg p-4 text-center">
            <p className="text-sm text-gray-600">
              <FormattedMessage id="steps.5.replay.runningHint" />
            </p>
            <p className="text-sm text-gray-600 mt-2">
              <FormattedMessage id="steps.5.description2" />
            </p>
          </div>
        </div>
      </div>
    );
  }

  // ---------------------------------------------------------------------------
  // Failed — show the error message + a Retry button. Retry kicks off a fresh trigger which
  // the backend treats as a new replay (the old FAILED record is preserved for debug).
  // ---------------------------------------------------------------------------
  if (latestReplay.status === 'FAILED') {
    return (
      <div className="bg-[#f3f4f6] -mx-4 -mt-4 px-4 py-6 min-h-full font-sans">
        <div className="bg-white rounded-lg shadow-sm border border-solid border-gray-200 p-6">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-xl font-bold text-[#1e1b4b]">
              <FormattedMessage id="steps.5.replay.heading" />
            </h2>
            <StatusBadge status={latestReplay.status} />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
            <div>
              <p className="text-sm text-gray-500">
                <FormattedMessage id="steps.5.replay.startedLabel" />
              </p>
              <p className="font-medium text-gray-900">
                {formatDateTime(
                  latestReplay.started_at || latestReplay.created_at
                )}
              </p>
            </div>
            <div>
              <p className="text-sm text-gray-500">
                <FormattedMessage id="steps.5.replay.failedAtLabel" />
              </p>
              <p className="font-medium text-gray-900">
                {formatDateTime(latestReplay.completed_at)}
              </p>
            </div>
            <div>
              <p className="text-sm text-gray-500">
                <FormattedMessage id="steps.5.replay.scaleFactorLabel" />
              </p>
              <p className="font-medium text-gray-900">
                {latestReplay.scale_factor}
              </p>
            </div>
          </div>

          {latestReplay.error_message && (
            <div className="bg-red-50 border border-solid border-red-200 rounded-lg p-4 mb-6">
              <p className="text-sm text-red-800 break-words">
                <span className="font-medium">
                  <FormattedMessage id="steps.5.replay.errorLabel" />:{' '}
                </span>
                {latestReplay.error_message}
              </p>
            </div>
          )}

          {triggerError && (
            <div className="bg-red-50 border border-solid border-red-200 rounded-lg p-3 mb-4 text-sm text-red-700">
              {triggerError}
            </div>
          )}

          <div className="flex justify-end">
            <button
              onClick={handleRetry}
              disabled={isTriggering}
              className="flex items-center gap-2 bg-[#1e1b4b] text-white px-6 py-2 rounded-lg hover:bg-blue-900 transition-colors font-medium text-sm disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <RefreshCw className="w-4 h-4" />
              {isTriggering ? (
                <FormattedMessage id="steps.5.replay.retryingButton" />
              ) : (
                <FormattedMessage id="steps.5.replay.retryButton" />
              )}
            </button>
          </div>
        </div>
      </div>
    );
  }

  // ---------------------------------------------------------------------------
  // Completed — summary cards from `metrics`, top contested tables from the fetched report,
  // and a Download Full Report button that re-mints a fresh presigned URL on click.
  // ---------------------------------------------------------------------------
  return (
    <div className="bg-[#f3f4f6] -mx-4 -mt-4 px-4 py-6 min-h-full font-sans">
      <div className="bg-white rounded-lg shadow-sm border border-solid border-gray-200 p-6">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-xl font-bold text-[#1e1b4b]">
            <FormattedMessage id="steps.5.replay.heading" />
          </h2>
          <StatusBadge status={latestReplay.status} />
        </div>

        <div className="flex flex-wrap items-center gap-4 text-sm text-gray-600 mb-6">
          <span>
            <FormattedMessage id="steps.5.replay.startedLabel" />:{' '}
            <span className="font-medium text-gray-900">
              {formatDateTime(latestReplay.started_at || latestReplay.created_at)}
            </span>
          </span>
          <span>
            <FormattedMessage id="steps.5.replay.completedLabel" />:{' '}
            <span className="font-medium text-gray-900">
              {formatDateTime(latestReplay.completed_at)}
            </span>
          </span>
          <span>
            <FormattedMessage id="steps.5.replay.durationLabel" />:{' '}
            <span className="font-medium text-gray-900">
              {formatDurationSeconds(metrics?.replay_wall_duration_seconds)}
            </span>
          </span>
          <span>
            <FormattedMessage id="steps.5.replay.scaleFactorLabel" />:{' '}
            <span className="font-medium text-gray-900">
              {latestReplay.scale_factor}
            </span>
          </span>
        </div>

        <div className="bg-gray-50 border border-solid border-gray-200 rounded-lg p-4 mb-6">
          <h3 className="font-semibold text-gray-900 mb-4">
            <FormattedMessage id="steps.5.replay.summaryHeading" />
          </h3>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div>
              <p className="text-sm text-gray-500">
                <FormattedMessage id="steps.5.replay.metric.queriesReplayed" />
              </p>
              <p className="font-bold text-lg text-gray-900">
                {(metrics?.replayed_queries ?? 0).toLocaleString()}
                {metrics?.total_queries != null && (
                  <span className="text-sm text-gray-500 font-medium ml-1">
                    / {metrics.total_queries.toLocaleString()}
                  </span>
                )}
              </p>
            </div>
            <div>
              <p className="text-sm text-gray-500">
                <FormattedMessage id="steps.5.replay.metric.lockEvents" />
              </p>
              <p className="font-bold text-lg text-gray-900">
                {(metrics?.lock_contention_count ?? 0).toLocaleString()}
              </p>
            </div>
            <div>
              <p className="text-sm text-gray-500">
                <FormattedMessage id="steps.5.replay.metric.lockWaitTotal" />
              </p>
              <p className="font-bold text-lg text-gray-900">
                {formatDurationSeconds(
                  metrics ? metrics.lock_wait_total_ms / 1000 : null
                )}
              </p>
            </div>
            <div>
              <p className="text-sm text-gray-500">
                <FormattedMessage id="steps.5.replay.metric.peakConnections" />
              </p>
              <p className="font-bold text-lg text-gray-900">
                {(metrics?.peak_concurrent_connections ?? 0).toLocaleString()}
              </p>
            </div>
          </div>
          {metrics && (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-4 border-t border-solid border-gray-200 pt-4">
              <div>
                <p className="text-sm text-gray-500">
                  <FormattedMessage id="steps.5.replay.metric.queriesWithErrors" />
                </p>
                <p className="font-bold text-lg text-red-600">
                  {(
                    (metrics.error_queries ?? 0) +
                    (metrics.timeout_queries ?? 0)
                  ).toLocaleString()}
                </p>
              </div>
              <div>
                <p className="text-sm text-gray-500">
                  <FormattedMessage id="steps.5.replay.metric.p50" />
                </p>
                <p className="font-medium text-gray-900">
                  {formatMs(metrics.p50_exec_time_ms)}
                </p>
              </div>
              <div>
                <p className="text-sm text-gray-500">
                  <FormattedMessage id="steps.5.replay.metric.p95" />
                </p>
                <p className="font-medium text-gray-900">
                  {formatMs(metrics.p95_exec_time_ms)}
                </p>
              </div>
              <div>
                <p className="text-sm text-gray-500">
                  <FormattedMessage id="steps.5.replay.metric.p99" />
                </p>
                <p className="font-medium text-gray-900">
                  {formatMs(metrics.p99_exec_time_ms)}
                </p>
              </div>
            </div>
          )}
        </div>

        <div className="mb-6">
          <h3 className="font-semibold text-gray-900 mb-4">
            <FormattedMessage id="steps.5.replay.topContentionHeading" />
          </h3>
          {isLoadingReport ? (
            <div className="border border-solid border-gray-200 rounded-lg p-6 text-center text-sm text-gray-500">
              <FormattedMessage id="steps.5.replay.topContentionLoading" />
            </div>
          ) : topContestedTables.length === 0 ? (
            <div className="border border-solid border-gray-200 rounded-lg p-6 text-center text-sm text-gray-500">
              <FormattedMessage id="steps.5.replay.topContentionEmpty" />
            </div>
          ) : (
            <div className="border border-solid border-gray-200 rounded-lg overflow-hidden">
              <table className="w-full">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-sm font-medium text-gray-600">
                      <FormattedMessage id="steps.5.replay.tableCol.table" />
                    </th>
                    <th className="px-4 py-3 text-left text-sm font-medium text-gray-600">
                      <span
                        className="cursor-help border-b border-dashed border-gray-400"
                        title="Sum of 500 ms polling observations. A lock held for 10 s contributes ~20 samples. The summary card shows the count of distinct waiter→blocker pairs."
                      >
                        <FormattedMessage id="steps.5.replay.tableCol.events" />
                      </span>
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {topContestedTables.map((entry) => (
                    <tr key={entry.table} className="hover:bg-gray-50">
                      <td className="px-4 py-3 text-sm font-mono text-gray-900 break-all">
                        {entry.table}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-900">
                        {entry.lock_event_samples.toLocaleString()}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {downloadError && (
          <div className="bg-red-50 border border-solid border-red-200 rounded-lg p-3 mb-4 text-sm text-red-700">
            {downloadError}
          </div>
        )}

        <div className="flex justify-end">
          <button
            onClick={handleDownloadReport}
            disabled={isDownloadingReport}
            className="flex items-center gap-2 bg-[#1e1b4b] text-white px-6 py-2 rounded-lg hover:bg-blue-900 transition-colors font-medium text-sm disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <Download className="w-4 h-4" />
            {isDownloadingReport ? (
              <FormattedMessage id="steps.5.replay.downloadingButton" />
            ) : (
              <FormattedMessage id="steps.5.replay.downloadButton" />
            )}
          </button>
        </div>
      </div>
    </div>
  );
}

export default QueryLogReplay;
